import { useMemo, useState } from "react";
import { useAppDispatch } from "../../../redux/store/hooks";
import { navigate } from "../../../redux/slice/layoutSlice";
import { setSelected } from "../../../redux/slice/selectionSlice";
import { useGetBrokersQuery, useDeleteBrokerMutation } from "../../../redux/api";
import type { Broker } from "../../../redux/api";
import {
  IconPlus,
  IconSearch,
  IconEdit,
  IconTrash,
} from "../../../components/icons";
import { RowMenu } from "../../../components/RowMenu";

const ENTITY = "broker";
const DETAIL_PATH = "/broker";
const COLUMN_COUNT = 6;

const statusTone = (status?: string | null) =>
  (status ?? "").toLowerCase() === "active" ? "badge--success" : "badge--warning";

// Platform Owner → Brokers (list page). Resellers / partners.
const Brokers = () => {
  const dispatch = useAppDispatch();

  const {
    data: brokers = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetBrokersQuery();

  const [deleteBroker] = useDeleteBrokerMutation();

  const [search, setSearch] = useState("");


  const openDetail = (id: string | null) => {
    dispatch(setSelected({ entity: ENTITY, id }));
    dispatch(navigate(DETAIL_PATH));
  };

  const handleDelete = async (broker: Broker) => {
    if (!window.confirm(`Delete "${broker.name}"?`)) return;
    try {
      await deleteBroker(broker.id).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to delete broker.",
      );
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return brokers;
    return brokers.filter((b: Broker) =>
      [b.name, b.companyName, b.email, b.referralCode, b.status].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      ),
    );
  }, [brokers, search]);

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load brokers"
    : null;

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Brokers</h2>
            <p className="panel__sub">
              Showing {visible.length} of {brokers.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search brokers…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => openDetail(null)}
            >
              <IconPlus /> New broker
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company</th>
                <th>Email</th>
                <th>Commission %</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading brokers…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {brokers.length === 0
                      ? "No brokers yet."
                      : "No brokers match your search."}
                  </td>
                </tr>
              ) : (
                visible.map((broker: Broker) => (
                  <tr key={broker.id}>
                    <td className="cell-title">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => openDetail(broker.id)}
                      >
                        {broker.name}
                      </button>
                    </td>
                    <td>{broker.companyName ?? "—"}</td>
                    <td className="cell-date">{broker.email ?? "—"}</td>
                    <td className="cell-date">{broker.commissionRate ?? 0}%</td>
                    <td>
                      <span className={`badge ${statusTone(broker.status)}`}>
                        <span className="badge__dot" />
                        {broker.status}
                      </span>
                    </td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "Edit broker",
                            icon: <IconEdit size={16} />,
                            onSelect: () => openDetail(broker.id),
                          },
                          {
                            label: "Delete broker",
                            icon: <IconTrash size={16} />,
                            onSelect: () => handleDelete(broker),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

    </div>
  );
};

export default Brokers;
